import React, { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// import images
import placeholderImage from '../../../images/static/collections_placeholder.jpg';

// import selectors
import {
  getUserCollections,
  cardExistsInCollectionById,
  getSavedInCollectionsByCardId,
} from 'js/store/collections/Collections.selectors';
import { getCardById } from 'js/store/cards/Cards.selectors';
import { getTranslationById } from 'js/store/app/App.selectors';

// import actions
import { addCardToCollection, removeCardFromCollection } from 'js/store/collections/Collections.actions';

// import components
import Thumbnail from 'js/components/thumbnail/Thumbnail';
import SwiperVirtual from 'js/components/swiper/Swiper.virtual';
import ButtonTickState from 'js/components/button/Button.tick-state';
import { trackEvent } from '../../utils/tracking';

const CollectionAddRemoveCarousel = ({ card, type = 'all', title = 'All Galleries' }) => {
  const { id: focusedCardId = '' } = card || {};
  const data = useSelector((state) =>
    type === 'saved-in' ? getSavedInCollectionsByCardId(state, focusedCardId) : getUserCollections(state)
  );

  // Memoize slides to prevent recreating on every render
  const slides = useMemo(() => {
    if (data.length === 0 && type === 'saved-in') {
      return [<CollectionPlaceholderSlide key="placeholder" />];
    }
    return data.map((collection) => (
      <CollectionSlide
        key={collection.id}
        cards={collection.cards}
        name={collection.name}
        collectionId={collection.id}
        focusedCardId={focusedCardId}
      />
    ));
  }, [data, focusedCardId, type]);

  return (
    <>
      <h2>{title}</h2>
      <SwiperVirtual slides={slides} />
    </>
  );
};

const CollectionSlide = ({ cards, name, collectionId, focusedCardId }) => {
  const dispatch = useDispatch();

  // Always call useSelector, but pass a safe value
  const firstCardId = cards?.[0] || null;
  const firstCard = useSelector((state) => (firstCardId ? getCardById(state, firstCardId) : null));
  const translatedName = useSelector((state) => getTranslationById(state, name, name));
  const { largeImage = placeholderImage } = firstCard?.front || {};

  const cardExists = useSelector((state) => cardExistsInCollectionById(state, collectionId, focusedCardId));

  const handleToggle = useCallback(() => {
    trackEvent(cardExists ? 'game_collection_remove_own' : 'game_collection_add_own', { content_id: focusedCardId });
    dispatch(
      cardExists
        ? removeCardFromCollection(collectionId, focusedCardId)
        : addCardToCollection(collectionId, focusedCardId)
    );
  }, [cardExists, focusedCardId, collectionId, dispatch]);

  return (
    <div style={{ position: 'relative' }}>
      <div className="card__save-action">
        <ButtonTickState action={handleToggle} selected={cardExists} />
      </div>
      <Thumbnail src={largeImage} alt={translatedName} caption={translatedName} />
    </div>
  );
};

const CollectionPlaceholderSlide = () => {
  const text = useSelector((state) =>
    getTranslationById(state, 'overlay_popup_not_yet_saved_in_any_galleries', 'Not yet saved in any galleries')
  );
  return (
    <div className="placeholder-slide--collection">
      <p>{text}</p>
    </div>
  );
};

export default CollectionAddRemoveCarousel;
